import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { facilityAdmin, staffQueue } from "../../api";
import { StatCard, PageHeader, LoadingSpinner } from "../../components/ui";
import { useAuth } from "../../context/AuthContext";
import BRAND from "../../brand";
import FacilityAdminDashboardModern from "./FacilityAdminDashboardModern";

function StaffAwayWidget() {
  const [awayStaff, setAwayStaff] = useState([]);
  useEffect(() => {
    facilityAdmin.listStaff?.().then(r => {
      const list = r?.data?.results || r?.data || [];
      setAwayStaff(list.filter(s => s.is_away));
    }).catch(() => {});
  }, []);
  if (awayStaff.length === 0) return null;
  return (
    <div style={{ background:"rgba(234,179,8,0.06)", border:"1px solid rgba(234,179,8,0.2)", borderRadius:"12px", padding:"14px 18px", marginBottom:"20px" }}>
      <div style={{ fontSize:"12px", fontWeight:"700", color:"#EAB308", letterSpacing:"0.06em", textTransform:"uppercase", marginBottom:"10px" }}>
        ☕ Staff On Break ({awayStaff.length})
      </div>
      <div style={{ display:"flex", flexWrap:"wrap", gap:"8px" }}>
        {awayStaff.map(s => (
          <div key={s.id} style={{ fontSize:"12px", color:"var(--text-secondary)", background:"rgba(234,179,8,0.08)", border:"1px solid rgba(234,179,8,0.15)", borderRadius:"6px", padding:"4px 10px" }}>
            {s.name} {s.station_name ? `— ${s.station_name}` : ""}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function FacilityAdminDashboard() {
  return BRAND.dashboard === "modern" ? <FacilityAdminDashboardModern /> : <ClassicDashboard />;
}

function ClassicDashboard() {
  const { user }   = useAuth();
  const navigate   = useNavigate();
  const [stats,    setStats]   = useState(null);
  const [loading,  setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      staffQueue.todayStats().catch(() => null),
      facilityAdmin.dailyReport().catch(() => null),
    ]).then(([, report]) => {
      setStats(report?.data || null);
    }).finally(() => setLoading(false));
  }, []);

  const SECTIONS = [
    { icon: "🗂️", title: "Zones & Stations",  desc: "Configure zones, stations, counters and outcomes", path: "/admin/zones" },
    { icon: "👤", title: "Staff Management",   desc: "Add staff, assign stations, reset passwords",       path: "/admin/staff" },
    { icon: "⚙️", title: "Queue Settings",     desc: "Issuance mode, priority rules, notifications",      path: "/admin/settings" },
    { icon: "📺", title: "Display Board",      desc: "Configure what appears on patient-facing TVs",      path: "/admin/display" },
    { icon: "🔊", title: "Audio & Sounds",     desc: "Upload chime, digit clips, station name recordings", path: "/admin/audio" },
    { icon: "📊", title: "Reports",            desc: "Daily volume, staff performance, bottleneck analysis", path: "/admin/reports" },
  ];

  return (
    <div style={styles.root}>
      <PageHeader
        title={user?.facilityName || "Dashboard"}
        subtitle={`Welcome back, ${user?.name}. Here's today's snapshot.`}
      />

      {loading ? <LoadingSpinner /> : stats ? (
        <div style={styles.statsRow}>
          <StatCard label="Tickets Today"   value={stats.total_tickets}  color="#8CBCE0" icon="🎫" />
          <StatCard label="Completed"        value={stats.completed}      color="#22C55E" icon="✅" />
          <StatCard label="In Queue"         value={stats.still_in_queue} color="#EAB308" icon="⏳" />
          <StatCard label="Abandoned"        value={stats.abandoned}      color="#EF4444" icon="🚶" />
          <StatCard label="Completion Rate"  value={stats.completion_rate != null ? `${stats.completion_rate}%` : null} color="#A78BFA" icon="📈" />
        </div>
      ) : (
        <div style={styles.noStats}>No activity yet today.</div>
      )}

      <StaffAwayWidget />
      <h2 style={styles.sectionTitle}>Manage</h2>
      <div style={styles.grid}>
        {SECTIONS.map(s => (
          <button key={s.path} onClick={() => navigate(s.path)} style={styles.card}>
            <span style={styles.cardIcon}>{s.icon}</span>
            <div style={styles.cardBody}>
              <div style={styles.cardTitle}>{s.title}</div>
              <div style={styles.cardDesc}>{s.desc}</div>
            </div>
            <span style={styles.cardArrow}>›</span>
          </button>
        ))}
      </div>
    </div>
  );
}

const styles = {
  root: { padding: "32px", fontFamily: "'DM Sans','Segoe UI',sans-serif", maxWidth: "1100px" },
  statsRow: { display: "flex", gap: "14px", flexWrap: "wrap", marginBottom: "32px" },
  noStats: { color: "var(--text-muted)", fontSize: "14px", marginBottom: "32px" },
  sectionTitle: { fontSize: "13px", fontWeight: "700", color: "var(--text-muted)", letterSpacing: "0.08em", textTransform: "uppercase", margin: "0 0 14px" },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px,1fr))", gap: "12px" },
  card: {
    display: "flex", alignItems: "center", gap: "14px",
    background: "var(--surface-bg)", border: "1px solid var(--surface-border)",
    borderRadius: "14px", padding: "18px 20px",
    cursor: "pointer", textAlign: "left", width: "100%",
    fontFamily: "inherit", transition: "border-color 0.15s, background 0.15s",
  },
  cardIcon:  { fontSize: "26px", flexShrink: 0 },
  cardBody:  { flex: 1, minWidth: 0 },
  cardTitle: { fontSize: "15px", fontWeight: "600", color: "var(--text-primary)", marginBottom: "3px" },
  cardDesc:  { fontSize: "12px", color: "var(--text-muted)", lineHeight: "1.4" },
  cardArrow: { fontSize: "20px", color: "var(--text-faint)", flexShrink: 0 },
};
